import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getAbout } from '../services/aboutService';
import { buildStorageUrl } from '../api/api';
import defaultImage from '../assets/images/misc/aboutus.jpg';

const About = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [hasFired, setHasFired] = useState(false);
    const sectionRef = useRef(null);
    const location = useLocation();

    // Trigger fetch if visible OR if hash matches this section
    useEffect(() => {
        if (hasFired) return;

        // On normal home load (no hash), fetch immediately instead of waiting for scroll.
        if (!location.hash) {
            setHasFired(true);
            return;
        }

        const targetId = 'about';

        // 1. Immediate match for target hash
        if (location.hash === `#${targetId}`) {
            setHasFired(true);
            return;
        }

        let observer = null;
        let timer = null;

        const startObserver = () => {
            observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setHasFired(true);
                        observer.disconnect();
                    }
                },
                { threshold: 0.1, rootMargin: '0px' } // Pre-load slightly before it enters
            );
            if (sectionRef.current) observer.observe(sectionRef.current);
        };

        // 2. Logic: If there is a hash for SOME OTHER section, we delay the observer
        if (location.hash && location.hash !== `#${targetId}`) {
            timer = setTimeout(startObserver, 2000);
        } else {
            startObserver();
        }

        return () => {
            if (timer) clearTimeout(timer);
            if (observer) observer.disconnect();
        };
    }, [hasFired, location.hash]);

    useEffect(() => {
        if (!hasFired) return;

        const fetchData = async () => {
            try {
                const result = await getAbout();
                // Handle different response structures (array or object wrapper)
                if (result.data && Array.isArray(result.data)) {
                    setData(result.data);
                } else if (Array.isArray(result)) {
                    setData(result);
                } else if (result) {
                    // Fallback for single object response, wrap in array
                    setData([result]);
                } else {
                    setData([]);
                }
            } catch (error) {
                console.error("Error loading About data", error);
                setError(error.message || "Failed to load Data");
            }
        };
        fetchData();
    }, [hasFired]);

    // Show nothing until the observer has triggered or hash matches
    if (!hasFired && !data) return <div ref={sectionRef} style={{ minHeight: '500px' }}></div>;

    if (error) {
        // Simple error indicator
        return (
             <section id="about" className="single-feature padding-medium mt-n5">
                <div className="container">
                    <div className="row">
                        <div className="col-12 text-center text-danger">
                            <p>Error: {error}</p>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (!data) return null; // Loading state

    if (data.length === 0) {
        return (
            <section id="about" className="single-feature padding-medium mt-n5">
                <div className="container">
                    <div className="row">
                        <div className="col-12 text-center">
                            <p>No About data found.</p>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    const getImageSrc = (image) => {
        if (!image) return defaultImage;
        console.log("Image name from backend:", image); // Debug image name
        if (image.startsWith("http")) return image;
        
        // Try to handle full local path if it comes like that
        try {
           // check if image is a local asset
           return require(`../assets/images/misc/${image}`);
        } catch (e) {
            // It's a backend image
            // We use the centralized base URL + specific folder for this section
            return buildStorageUrl('aboutSection', image);
        }
    }

    return (
        <div ref={sectionRef}>
            {data.map((item, index) => (
                <section id={index === 0 ? "about" : `about-${index}`} className={`single-feature ${index === 0 ? 'padding-bottom-medium' : 'padding-medium'}`} key={index}>
                    <div className="container">
                        <div className="row d-flex align-items-center">
                            <div className={`col-lg-6 text-sm-left text-center wow ${index % 2 === 0 ? 'fadeInLeft pr-lg-5' : 'fadeInRight order-lg-2 pl-lg-5'}`} data-wow-delay="300ms">
                                <div className="heading-title mb-4">
                                    <h2 className="darkcolor font-normal bottom30">
                                        <span className="defaultcolor font-bold">{item.title}</span> <br /> {item.name}
                                    </h2>
                                </div>
                                <div className="bottom35" dangerouslySetInnerHTML={{ __html: item.description }}></div>
                                <Link to={item.button_link} className="button gradient-btn mb-sm-0 mb-4">{item.button_name}</Link>
                            </div>
                            <div className={`col-lg-6 wow ${index % 2 === 0 ? 'fadeInRight pl-lg-5' : 'fadeInLeft order-lg-1 pr-lg-5'}`} data-wow-delay="300ms">
                                <div className="image text-center">
                                    <img 
                                        alt={item.name} 
                                        src={getImageSrc(item.image)} 
                                        className="img-fluid" 
                                        style={{ width: '100%', maxWidth: '540px', height: 'auto' }} 
                                        onError={(e) => { e.target.onerror = null; e.target.src = defaultImage; }} 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            ))}
        </div>
    );
};

export default About;
